import { useState, useEffect, useRef } from "react"
import {
  Image,
  AlertTriangle,
  Check,
  X as XIcon,
  Upload,
  Trash2,
  ArrowLeft,
  FileImage,
  FileVideo,
  Filter,
  CirclePlay,
} from "lucide-react"
import {
  getExerciseLibraryWithMedia,
  getExerciseMediaUrls,
  uploadExerciseMedia,
  deleteExerciseMedia as apiDeleteMedia,
  bulkAssignExerciseMedia,
  bulkClearExerciseMedia,
  type ExerciseLibraryEntry,
  type ExerciseMediaWithUrls,
} from "@/lib/api"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { SafetyDialog } from "@/components/ConfirmDialog"
import { useReadOnly } from "@/lib/read-only"

type MediaFilter = "all" | "has_media" | "missing_media" | "missing_video" | "missing_image" | "no_demo"

function MediaDetail({
  exercise,
  onClose,
  onMediaChanged,
}: {
  exercise: ExerciseLibraryEntry
  onClose: () => void
  onMediaChanged: () => void
}) {
  const { readOnly } = useReadOnly()
  const [mediaData, setMediaData] = useState<ExerciseMediaWithUrls | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [noMediaRow, setNoMediaRow] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<"image" | "video" | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)

  const fetchUrls = async () => {
    setIsLoading(true)
    setNoMediaRow(false)
    try {
      const data = await getExerciseMediaUrls(exercise.exercise_name)
      setMediaData(data)
    } catch {
      // No media row exists yet — that's OK for exercises without media
      setNoMediaRow(true)
      setMediaData(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchUrls()
  }, [exercise.exercise_name])

  const handleUpload = async (file: File, mediaType: "image" | "video") => {
    setIsUploading(true)
    try {
      await uploadExerciseMedia(exercise.exercise_name, file, mediaType)
      toast.success(`${mediaType} uploaded successfully`)
      setNoMediaRow(false)
      await fetchUrls()
      onMediaChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed")
    } finally {
      setIsUploading(false)
    }
  }

  const handleDelete = async (mediaType: "image" | "video") => {
    setIsDeleting(true)
    try {
      await apiDeleteMedia(exercise.exercise_name, mediaType)
      toast.success(`${mediaType} deleted`)
      setDeleteConfirm(null)
      await fetchUrls()
      onMediaChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed")
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onClose} className="p-1 rounded hover:bg-accent">
          <ArrowLeft className="size-4" />
        </button>
        <div>
          <h2 className="text-xl font-bold">{exercise.exercise_name}</h2>
          <p className="text-sm text-muted-foreground">
            {exercise.movement_pattern} / {exercise.equipment}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <div className="h-64 bg-muted animate-pulse rounded-lg" />
          <div className="h-48 bg-muted animate-pulse rounded-lg" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="lg:col-span-2 rounded-lg border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CirclePlay className="size-4 text-muted-foreground" />
                <h3 className="font-semibold text-sm">YouTube demo</h3>
              </div>
              {exercise.youtube_url && (
                <a
                  href={exercise.youtube_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  Open on YouTube
                </a>
              )}
            </div>
            {exercise.youtube_id ? (
              <div className="aspect-video max-w-2xl rounded-md overflow-hidden border bg-muted">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${exercise.youtube_id}`}
                  title={`${exercise.exercise_name} demo`}
                  className="size-full"
                  allow="encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-24 rounded-md border border-dashed text-muted-foreground">
                <p className="text-xs">No demo</p>
              </div>
            )}
          </div>
          <div className="rounded-lg border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileImage className="size-4 text-muted-foreground" />
                <h3 className="font-semibold text-sm">Image</h3>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => imageInputRef.current?.click()}
                  disabled={readOnly || isUploading}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-accent disabled:opacity-50"
                >
                  <Upload className="size-3" />
                  Upload
                </button>
                {mediaData?.image_filename && (
                  <button
                    onClick={() => setDeleteConfirm("image")}
                    disabled={readOnly}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-destructive/30 text-destructive rounded hover:bg-destructive/10"
                  >
                    <Trash2 className="size-3" />
                  </button>
                )}
              </div>
            </div>

            <input
              ref={imageInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleUpload(file, "image")
                e.target.value = ""
              }}
            />

            {mediaData?.image_url ? (
              <div className="rounded-md overflow-hidden border bg-muted">
                <img
                  src={mediaData.image_url}
                  alt={exercise.exercise_name}
                  className="w-full h-auto max-h-80 object-contain"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 rounded-md border border-dashed text-muted-foreground">
                <FileImage className="size-8 mb-2 opacity-40" />
                <p className="text-xs">No image uploaded</p>
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="mt-2 text-xs text-primary hover:underline"
                >
                  Upload one
                </button>
              </div>
            )}

            {mediaData?.image_filename && (
              <p className="text-xs text-muted-foreground truncate">{mediaData.image_filename}</p>
            )}
          </div>

          <div className="rounded-lg border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileVideo className="size-4 text-muted-foreground" />
                <h3 className="font-semibold text-sm">Video</h3>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => videoInputRef.current?.click()}
                  disabled={readOnly || isUploading}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs border rounded hover:bg-accent disabled:opacity-50"
                >
                  <Upload className="size-3" />
                  Upload
                </button>
                {mediaData?.video_filename && (
                  <button
                    onClick={() => setDeleteConfirm("video")}
                    disabled={readOnly}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-destructive/30 text-destructive rounded hover:bg-destructive/10"
                  >
                    <Trash2 className="size-3" />
                  </button>
                )}
              </div>
            </div>

            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleUpload(file, "video")
                e.target.value = ""
              }}
            />

            {mediaData?.video_url ? (
              <div className="rounded-md overflow-hidden border bg-black">
                <video src={mediaData.video_url} controls className="w-full max-h-80">
                  Your browser does not support video playback.
                </video>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 rounded-md border border-dashed text-muted-foreground">
                <FileVideo className="size-8 mb-2 opacity-40" />
                <p className="text-xs">No video uploaded</p>
                <button
                  onClick={() => videoInputRef.current?.click()}
                  className="mt-2 text-xs text-primary hover:underline"
                >
                  Upload one
                </button>
              </div>
            )}

            {mediaData?.video_filename && (
              <p className="text-xs text-muted-foreground truncate">{mediaData.video_filename}</p>
            )}
          </div>
        </div>
      )}

      {noMediaRow && (
        <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2">
          <p className="text-xs text-blue-700">
            No media record exists yet for this exercise. Uploading will create one automatically.
          </p>
        </div>
      )}

      {isUploading && (
        <div className="fixed inset-0 bg-background/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border shadow-lg p-6 flex items-center gap-3">
            <div className="animate-spin rounded-full size-5 border-b-2 border-primary" />
            <span className="text-sm">Uploading...</span>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <SafetyDialog
          title={`Delete ${deleteConfirm} for ${exercise.exercise_name}`}
          description={`This will permanently remove the ${deleteConfirm} file from Supabase Storage. The mobile app will no longer show this ${deleteConfirm} for the exercise.`}
          warnings={[
            "This action cannot be undone",
            "The file will be deleted from storage immediately",
            "Users will see a missing media placeholder in the app",
          ]}
          confirmLabel={`Delete ${deleteConfirm}`}
          variant="destructive"
          typeToConfirm="DELETE"
          onConfirm={() => handleDelete(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
          isLoading={isDeleting}
        />
      )}
    </div>
  )
}

const FILTER_OPTIONS: { value: MediaFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "has_media", label: "Has Media" },
  { value: "missing_media", label: "No Media" },
  { value: "missing_video", label: "Missing Video" },
  { value: "missing_image", label: "Missing Image" },
  { value: "no_demo", label: "No Demo" },
]

export function MediaLibraryPage() {
  const [exercises, setExercises] = useState<ExerciseLibraryEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState("")
  const [mediaFilter, setMediaFilter] = useState<MediaFilter>("all")
  const [movementFilter, setMovementFilter] = useState<string>("all")
  const [equipmentFilter, setEquipmentFilter] = useState<string>("all")
  const [selected, setSelected] = useState<ExerciseLibraryEntry | null>(null)
  const [stats, setStats] = useState({ total: 0, with_video: 0, with_image: 0 })
  const { readOnly } = useReadOnly()

  const [selectedNames, setSelectedNames] = useState<Set<string>>(new Set())
  const [anchorName, setAnchorName] = useState<string | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [confirmClear, setConfirmClear] = useState<"image" | "video" | null>(null)
  const bulkVideoInputRef = useRef<HTMLInputElement>(null)
  const bulkImageInputRef = useRef<HTMLInputElement>(null)
  const pendingNamesRef = useRef<string[]>([])

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const data = await getExerciseLibraryWithMedia()
      setExercises(data.exercises)
      setStats({ total: data.total, with_video: data.with_video, with_image: data.with_image })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load exercise library")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const movements = [...new Set(exercises.map((e) => e.movement_pattern))].sort()
  const equipments = [...new Set(exercises.map((e) => e.equipment))].sort()

  const filtered = exercises.filter((e) => {
    if (search && !e.exercise_name.toLowerCase().includes(search.toLowerCase())) return false
    if (movementFilter !== "all" && e.movement_pattern !== movementFilter) return false
    if (equipmentFilter !== "all" && e.equipment !== equipmentFilter) return false
    if (mediaFilter === "has_media" && !e.has_video && !e.has_image) return false
    if (mediaFilter === "missing_media" && (e.has_video || e.has_image)) return false
    if (mediaFilter === "missing_video" && e.has_video) return false
    if (mediaFilter === "missing_image" && e.has_image) return false
    if (mediaFilter === "no_demo" && e.youtube_id) return false
    return true
  })

  // Prune the selection to currently-visible rows whenever the filters change.
  useEffect(() => {
    const vis = new Set(filtered.map((e) => e.exercise_name))
    setSelectedNames((prev) => {
      const next = new Set([...prev].filter((n) => vis.has(n)))
      return next.size === prev.size ? prev : next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, mediaFilter, movementFilter, equipmentFilter, exercises])

  const allVisibleSelected = filtered.length > 0 && filtered.every((e) => selectedNames.has(e.exercise_name))
  const someVisibleSelected = filtered.some((e) => selectedNames.has(e.exercise_name))

  const onRowSelect = (ev: React.MouseEvent, entry: ExerciseLibraryEntry, idx: number) => {
    const name = entry.exercise_name
    const anchorIdx = anchorName != null ? filtered.findIndex((e) => e.exercise_name === anchorName) : -1
    if (ev.shiftKey && anchorIdx >= 0) {
      const [a, b] = anchorIdx <= idx ? [anchorIdx, idx] : [idx, anchorIdx]
      const range = filtered.slice(a, b + 1).map((e) => e.exercise_name)
      setSelectedNames((prev) => new Set([...prev, ...range]))
    } else {
      setSelectedNames((prev) => {
        const n = new Set(prev)
        n.has(name) ? n.delete(name) : n.add(name)
        return n
      })
      setAnchorName(name)
    }
  }
  const toggleSelectAll = () => {
    setSelectedNames((prev) => {
      if (allVisibleSelected) {
        const n = new Set(prev)
        filtered.forEach((e) => n.delete(e.exercise_name))
        return n
      }
      return new Set([...prev, ...filtered.map((e) => e.exercise_name)])
    })
  }
  const clearSelection = () => { setSelectedNames(new Set()); setAnchorName(null) }

  const runBulkAssign = async (file: File, mediaType: "image" | "video") => {
    const names = pendingNamesRef.current
    if (readOnly || bulkBusy || names.length === 0) return
    setBulkBusy(true)
    try {
      const res = await bulkAssignExerciseMedia(names, file, mediaType)
      toast.success(`Assigned ${mediaType} to ${res.count} exercise(s)`)
      clearSelection()
      await fetchData()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk assign failed")
    } finally {
      setBulkBusy(false)
    }
  }
  const assignToSelection = (mediaType: "image" | "video") => {
    pendingNamesRef.current = [...selectedNames]
    if (pendingNamesRef.current.length === 0) return
    ;(mediaType === "video" ? bulkVideoInputRef : bulkImageInputRef).current?.click()
  }
  // Assign one file to every visible exercise of the active equipment that lacks this media type.
  const setEquipmentDefault = (mediaType: "image" | "video") => {
    if (equipmentFilter === "all") return
    pendingNamesRef.current = filtered
      .filter((e) => (mediaType === "video" ? !e.has_video : !e.has_image))
      .map((e) => e.exercise_name)
    if (pendingNamesRef.current.length === 0) {
      toast.info(`Every ${equipmentFilter} exercise already has a ${mediaType}`)
      return
    }
    ;(mediaType === "video" ? bulkVideoInputRef : bulkImageInputRef).current?.click()
  }
  const runBulkClear = async (mediaType: "image" | "video") => {
    const names = [...selectedNames]
    if (readOnly || bulkBusy || names.length === 0) return
    setBulkBusy(true)
    try {
      const res = await bulkClearExerciseMedia(names, mediaType)
      toast.success(`Cleared ${mediaType} for ${res.count} exercise(s)`)
      setConfirmClear(null)
      clearSelection()
      await fetchData()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk clear failed")
    } finally {
      setBulkBusy(false)
    }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Media Library</h1>
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-10 bg-muted animate-pulse rounded" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Media Library</h1>
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
          <AlertTriangle className="size-8 mx-auto mb-2 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    )
  }

  if (selected) {
    return (
      <div className="p-6">
        <MediaDetail
          exercise={selected}
          onClose={() => setSelected(null)}
          onMediaChanged={fetchData}
        />
      </div>
    )
  }

  const coveragePct = stats.total > 0 ? ((stats.with_video / stats.total) * 100).toFixed(1) : "0"

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Image className="size-6 text-muted-foreground" />
          <h1 className="text-2xl font-bold">Media Library</h1>
        </div>
        <div className="flex gap-4 text-xs text-muted-foreground">
          <span>{stats.total} exercises</span>
          <span>{stats.with_video} videos ({coveragePct}%)</span>
          <span>{stats.with_image} images</span>
        </div>
      </div>

      <div className="mb-4 rounded-lg border bg-card p-3">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
          <span>Video coverage</span>
          <span>{stats.with_video} / {stats.total}</span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-green-500 transition-all"
            style={{ width: `${coveragePct}%` }}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="Search exercises..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-xs px-3 py-2 text-sm border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-primary"
        />

        <div className="flex items-center gap-1.5">
          <Filter className="size-3.5 text-muted-foreground" />
          <select
            value={mediaFilter}
            onChange={(e) => setMediaFilter(e.target.value as MediaFilter)}
            className="text-sm border rounded-md px-2 py-1.5 bg-background"
          >
            {FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <select
          value={movementFilter}
          onChange={(e) => setMovementFilter(e.target.value)}
          className="text-sm border rounded-md px-2 py-1.5 bg-background"
        >
          <option value="all">All patterns</option>
          {movements.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>

        <select
          value={equipmentFilter}
          onChange={(e) => setEquipmentFilter(e.target.value)}
          className="text-sm border rounded-md px-2 py-1.5 bg-background"
        >
          <option value="all">All equipment</option>
          {equipments.map((eq) => (
            <option key={eq} value={eq}>{eq}</option>
          ))}
        </select>

        {equipmentFilter !== "all" && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setEquipmentDefault("video")}
              disabled={readOnly || bulkBusy}
              className="inline-flex items-center gap-1 px-2 py-1.5 text-xs border rounded-md hover:bg-accent disabled:opacity-50"
              title={`Upload one video as the default for all ${equipmentFilter} exercises missing one`}
            >
              <FileVideo className="size-3.5" />
              Default video
            </button>
            <button
              onClick={() => setEquipmentDefault("image")}
              disabled={readOnly || bulkBusy}
              className="inline-flex items-center gap-1 px-2 py-1.5 text-xs border rounded-md hover:bg-accent disabled:opacity-50"
              title={`Upload one image as the default for all ${equipmentFilter} exercises missing one`}
            >
              <FileImage className="size-3.5" />
              Default image
            </button>
          </div>
        )}

        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} shown
        </span>
      </div>

      <input
        ref={bulkVideoInputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/webm"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) runBulkAssign(file, "video")
          e.target.value = ""
        }}
      />
      <input
        ref={bulkImageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) runBulkAssign(file, "image")
          e.target.value = ""
        }}
      />

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="w-10 px-3 py-3">
                <input
                  type="checkbox"
                  className="size-4 cursor-pointer align-middle"
                  checked={allVisibleSelected}
                  ref={(el) => { if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected }}
                  onChange={toggleSelectAll}
                  aria-label="Select all visible exercises"
                />
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Exercise</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Pattern</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Equipment</th>
              <th className="text-center px-4 py-3 font-medium text-muted-foreground">Video</th>
              <th className="text-center px-4 py-3 font-medium text-muted-foreground">Image</th>
              <th className="text-center px-4 py-3 font-medium text-muted-foreground">YouTube</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e, idx) => {
              const isSel = selectedNames.has(e.exercise_name)
              return (
              <tr
                key={e.exercise_name}
                className={cn(
                  "border-b cursor-pointer transition-colors",
                  isSel
                    ? "bg-primary/5 hover:bg-primary/10"
                    : e.has_video || e.has_image
                    ? "hover:bg-accent/50"
                    : "hover:bg-accent/50 bg-muted/20",
                )}
                onClick={() => setSelected(e)}
              >
                <td className="w-10 px-3 py-2.5" onClick={(ev) => ev.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="size-4 cursor-pointer align-middle"
                    checked={isSel}
                    onClick={(ev) => onRowSelect(ev, e, idx)}
                    onChange={() => {}}
                    aria-label={`Select ${e.exercise_name}`}
                  />
                </td>
                <td className="px-4 py-2.5 font-medium">{e.exercise_name}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{e.movement_pattern}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{e.equipment}</td>
                <td className="px-4 py-2.5 text-center">
                  {e.has_video ? (
                    <Check className="size-4 text-green-600 mx-auto" />
                  ) : (
                    <XIcon className="size-4 text-muted-foreground/30 mx-auto" />
                  )}
                </td>
                <td className="px-4 py-2.5 text-center">
                  {e.has_image ? (
                    <Check className="size-4 text-green-600 mx-auto" />
                  ) : (
                    <XIcon className="size-4 text-muted-foreground/30 mx-auto" />
                  )}
                </td>
                <td className="px-4 py-2.5 text-center">
                  {e.youtube_id ? (
                    <CirclePlay className="size-4 text-red-600 mx-auto" aria-label="Has YouTube demo" />
                  ) : (
                    <XIcon className="size-4 text-muted-foreground/30 mx-auto" aria-label="No demo" />
                  )}
                </td>
              </tr>
              )
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  No exercises match your filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedNames.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 rounded-full border bg-card px-3 py-2 shadow-lg">
          <span className="text-sm font-medium px-1">{selectedNames.size} selected</span>
          <span className="text-muted-foreground">|</span>
          <button
            onClick={() => assignToSelection("video")}
            disabled={readOnly || bulkBusy}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border hover:bg-accent disabled:opacity-50"
            title="Upload one video and assign it to all selected exercises"
          >
            <FileVideo className="size-3.5" /> Assign video
          </button>
          <button
            onClick={() => assignToSelection("image")}
            disabled={readOnly || bulkBusy}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border hover:bg-accent disabled:opacity-50"
            title="Upload one image and assign it to all selected exercises"
          >
            <FileImage className="size-3.5" /> Assign image
          </button>
          <button
            onClick={() => setConfirmClear("video")}
            disabled={readOnly || bulkBusy}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-destructive/30 text-destructive hover:bg-destructive/10 disabled:opacity-50"
            title="Remove the video from all selected exercises"
          >
            <Trash2 className="size-3.5" /> Clear video
          </button>
          <button
            onClick={() => setConfirmClear("image")}
            disabled={readOnly || bulkBusy}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-destructive/30 text-destructive hover:bg-destructive/10 disabled:opacity-50"
            title="Remove the image from all selected exercises"
          >
            <Trash2 className="size-3.5" /> Clear image
          </button>
          <button
            onClick={clearSelection}
            className="text-muted-foreground hover:text-foreground p-1"
            title="Clear selection"
            aria-label="Clear selection"
          >
            <XIcon className="size-4" />
          </button>
        </div>
      )}

      {bulkBusy && (
        <div className="fixed inset-0 bg-background/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border shadow-lg p-6 flex items-center gap-3">
            <div className="animate-spin rounded-full size-5 border-b-2 border-primary" />
            <span className="text-sm">Applying to {pendingNamesRef.current.length || selectedNames.size} exercises...</span>
          </div>
        </div>
      )}

      {confirmClear && (
        <SafetyDialog
          title={`Clear ${confirmClear} for ${selectedNames.size} exercise(s)?`}
          description={`This removes the ${confirmClear} reference from each selected exercise. The mobile app will stop showing this ${confirmClear} for them. The underlying file stays in storage and can be re-assigned.`}
          warnings={[`${selectedNames.size} exercises affected.`]}
          confirmLabel={`Clear ${confirmClear}`}
          variant="destructive"
          onConfirm={() => runBulkClear(confirmClear)}
          onCancel={() => setConfirmClear(null)}
          isLoading={bulkBusy}
        />
      )}
    </div>
  )
}
